import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash, PencilSimple, Package } from "@phosphor-icons/react";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const blank = () => ({ name: "", description: "", base_price: 0, itinerary: [], addons: [], active: true });

/** Priced options under one itinerary item, or the package's add-on list. */
function PricedRows({ rows, onChange, addLabel, placeholder, testid }) {
  const patch = (i, key, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)));
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={r.label ?? ""}
            placeholder={placeholder}
            onChange={(e) => patch(i, "label", e.target.value)}
            className="flex-1 bg-input border-border"
            data-testid={`${testid}-label-${i}`}
          />
          <span className="text-xs text-muted-foreground" aria-hidden="true">$</span>
          <Input
            type="number" step="0.01" min="0"
            value={r.price ?? ""}
            onChange={(e) => patch(i, "price", e.target.value)}
            className="w-28 text-right tnum bg-input border-border"
            aria-label={`Price for ${r.label || `option ${i + 1}`}`}
            data-testid={`${testid}-price-${i}`}
          />
          <button
            type="button"
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
            className="text-muted-foreground hover:text-red-400 p-1"
            aria-label={`Remove ${r.label || `option ${i + 1}`}`}
            data-testid={`${testid}-remove-${i}`}
          >
            <Trash size={14} />
          </button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm"
        onClick={() => onChange([...rows, { label: "", price: 0 }])}
        data-testid={`${testid}-add`}>
        <Plus size={13} className="mr-1.5" /> {addLabel}
      </Button>
    </div>
  );
}

function PackageDialog({ open, onClose, editing }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(() => (editing ? { ...blank(), ...editing } : blank()));
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const items = form.itinerary || [];

  const patchItem = (i, patch) =>
    set({ itinerary: items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        base_price: Number(form.base_price) || 0,
        itinerary: items.map((it) => ({
          ...it,
          options: (it.options || []).map((o) => ({ ...o, price: Number(o.price) || 0 })),
        })),
        addons: (form.addons || []).map((a) => ({ ...a, price: Number(a.price) || 0 })),
      };
      return editing?.id
        ? (await api.put(`/tour-packages/${editing.id}`, body)).data
        : (await api.post("/tour-packages", body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tour-packages"] });
      toast.success(editing?.id ? "Package updated" : "Package created");
      onClose();
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="package-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {editing?.id ? "Edit package" : "New tour package"}
          </DialogTitle>
          <DialogDescription>
            Name it, set a base price, then add each stop with the choices available there and a price for each.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <Label className="label-mono mb-1.5 block">Package name<span className="text-primary"> *</span></Label>
              <Input value={form.name} onChange={(e) => set({ name: e.target.value })}
                placeholder="e.g. Niagara Day Tour" className="bg-input border-border"
                data-testid="package-name" />
            </div>
            <div>
              <Label className="label-mono mb-1.5 block">Base price</Label>
              <Input type="number" step="0.01" min="0" value={form.base_price ?? ""}
                onChange={(e) => set({ base_price: e.target.value })}
                className="bg-input border-border text-right tnum" data-testid="package-base-price" />
            </div>
          </div>

          <div>
            <Label className="label-mono mb-1.5 block">Description</Label>
            <Textarea rows={2} value={form.description ?? ""}
              onChange={(e) => set({ description: e.target.value })}
              className="bg-input border-border" data-testid="package-description" />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="label-mono">Itinerary</span>
              <Button type="button" variant="outline" size="sm"
                onClick={() => set({ itinerary: [...items, { label: "", options: [] }] })}
                data-testid="package-item-add">
                <Plus size={13} className="mr-1.5" /> Add itinerary item
              </Button>
            </div>
            {items.length === 0 && (
              <p className="text-xs text-muted-foreground" data-testid="package-itinerary-empty">
                Add a stop — "Restaurant stop", "Scenery spot" — then list the choices under it with a price each.
              </p>
            )}
            {items.map((item, i) => (
              <div key={i} className="rounded-2xl border border-border bg-card/40 p-4 space-y-3"
                data-testid={`package-item-${i}`}>
                <div className="flex items-center gap-2">
                  <Input value={item.label ?? ""} placeholder="Stop name, e.g. Restaurant stop"
                    onChange={(e) => patchItem(i, { label: e.target.value })}
                    className="flex-1 bg-input border-border" data-testid={`package-item-label-${i}`} />
                  <button type="button"
                    onClick={() => set({ itinerary: items.filter((_, j) => j !== i) })}
                    className="text-muted-foreground hover:text-red-400 p-1"
                    aria-label={`Remove ${item.label || `itinerary item ${i + 1}`}`}
                    data-testid={`package-item-remove-${i}`}>
                    <Trash size={15} />
                  </button>
                </div>
                <PricedRows
                  rows={item.options || []}
                  onChange={(options) => patchItem(i, { options })}
                  addLabel="Add an option"
                  placeholder="e.g. The Keg"
                  testid={`package-item-${i}-options`}
                />
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <span className="label-mono">Package add-ons</span>
            <PricedRows
              rows={form.addons || []}
              onChange={(addons) => set({ addons })}
              addLabel="Add an add-on"
              placeholder="e.g. Extra hour of photos"
              testid="package-addons"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="package-cancel">Cancel</Button>
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || !form.name.trim()}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="package-save"
          >
            {save.isPending ? "Saving…" : "Save package"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TourPackages() {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null); // null | {} | package
  const { data: packages = [], isLoading } = useQuery({
    queryKey: ["tour-packages"],
    queryFn: async () => (await api.get("/tour-packages")).data,
  });

  const archive = useMutation({
    mutationFn: async (id) => (await api.delete(`/tour-packages/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tour-packages"] });
      toast.success("Package archived — bookings that used it keep their prices");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const optionCount = (p) => (p.itinerary || []).reduce((s, i) => s + (i.options || []).length, 0);

  return (
    <div className="space-y-5" data-testid="tour-packages-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-xl">
          Build a package once, then price a tour off it. Each itinerary item holds the choices
          for that stop with a price each, and the booking form copies whatever the client picks.
        </p>
        <Button onClick={() => setDialog({})}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="new-package-button">
          <Plus size={15} className="mr-1.5" /> New package
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground" data-testid="packages-loading">Loading packages…</p>
      ) : packages.length === 0 ? (
        <div className="border border-dashed border-border rounded-2xl p-10 text-center" data-testid="packages-empty">
          <Package size={26} className="mx-auto text-muted-foreground mb-3" />
          <p className="font-heading text-xl">No packages yet</p>
          <p className="text-sm text-muted-foreground mt-1.5">
            Build your first one — name it, set a base price, then add the stops and their options.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {packages.map((p) => (
            <div key={p.id} className="border border-border bg-card rounded-2xl p-5" data-testid={`package-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-heading text-xl">{p.name}</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Base <span className="tnum text-foreground">{money(p.base_price)}</span>
                    {" · "}{(p.itinerary || []).length} stop{(p.itinerary || []).length === 1 ? "" : "s"}
                    {" · "}{optionCount(p)} option{optionCount(p) === 1 ? "" : "s"}
                    {" · "}{(p.addons || []).length} add-on{(p.addons || []).length === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setDialog(p)} className="p-2 text-muted-foreground hover:text-foreground"
                    aria-label={`Edit ${p.name}`} data-testid={`package-edit-${p.id}`}>
                    <PencilSimple size={15} />
                  </button>
                  <button onClick={() => archive.mutate(p.id)} className="p-2 text-muted-foreground hover:text-red-400"
                    aria-label={`Archive ${p.name}`} data-testid={`package-archive-${p.id}`}>
                    <Trash size={15} />
                  </button>
                </div>
              </div>
              {(p.itinerary || []).length > 0 && (
                <div className="mt-4 space-y-2">
                  {p.itinerary.map((item) => (
                    <div key={item.id} className="text-sm">
                      <span className="label-mono">{item.label}</span>
                      <span className="text-muted-foreground">
                        {" "}{(item.options || []).map((o) => `${o.label} ${money(o.price)}`).join(" · ") || "no options"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {dialog && (
        <PackageDialog
          open
          editing={dialog.id ? dialog : null}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
